import { createSlice } from '@reduxjs/toolkit';
import {
  ExternalSystemInstanceType,
  GlobalExternalSystemInstanceType,
} from '../../types/external-system-instance/external-system-instance.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import {
  deleteCarrierPlanConfig,
  deleteExternalSystemDetails,
  deleteRouteConfig,
  getExternalCarrierMapperList,
  getExternalSystemInstanceDetails,
  getExternalSystemMapperList,
  getExternalSystems,
  getExternalSystemsPBX,
  getExternalSystemsProviders,
  getGlobalExternalSystems,
  getPBXDomainsList,
  patchExternalSystemDetails,
  patchGlobalExternalSystemDetails,
  patchRouteConfig,
  postExternalSystemDetails,
  createCarrierPlanConfig,
  postPBXDomains,
  postRouteConfig,
  updateCarrierPlanConfig,
  createCSRPConfig,
  getCSRPConfigsList,
  deleteCSRPConfig,
  updateCSRPConfig,
} from './externalSystems.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import { MobileCarrierPlanConfig } from '../../types/external-system-instance/mobile-carrier-plan-config';
import { CSRPConfigType } from '../../types/external-system-instance/csrp-config.type';

export interface ExternalSystemsState {
  externalSystemsList: Array<ExternalSystemInstanceType> | null;
  externalSystemsListFetchStatus: FETCH_STATUS;
  externalSystemInstanceDetails: ExternalSystemInstanceType | null;
  externalSystemInstanceDetailsFetchStatus: FETCH_STATUS;
  globalExternalSystemsList: Array<GlobalExternalSystemInstanceType> | null;
  globalExternalSystemsListFetchStatus: FETCH_STATUS;
  externalSystemDetailsPostStatus: FETCH_STATUS;
  externalSystemDetailsDeleteStatus: FETCH_STATUS;
  externalSystemDetailsPatchStatus: FETCH_STATUS;
  globalExternalSystemDetailsPatchStatus: FETCH_STATUS;
  systemMapperList: Array<ExternalSystemInstanceType> | null;
  systemMapperListFetchStatus: FETCH_STATUS;
  carrierMapperList: MobileCarrierPlanConfig[] | null;
  carrierMapperListFetchStatus: FETCH_STATUS;

  csrpMapperList: CSRPConfigType[] | null;
  csrpMapperListFetchStatus: FETCH_STATUS;
  carrierMapperDetailsPostStatus: FETCH_STATUS;
  carrierMapperDetailsPatchStatus: FETCH_STATUS;
  carrierMapperDetailsDeleteStatus: FETCH_STATUS;
  externalSystemProvidersList: Array<ExternalSystemInstanceType> | null;
  externalSystemProvidersListFetchStatus: FETCH_STATUS;
  externalSystemPBXList: Array<ExternalSystemInstanceType> | null;
  externalSystemPBXListFetchStatus: FETCH_STATUS;
  routingConfigPostStatus: FETCH_STATUS;
  routingConfigDeleteStatus: FETCH_STATUS;
  csrpConfigDeleteStatus: FETCH_STATUS;
  routingConfigPatchStatus: FETCH_STATUS;
  pbxDomainsList: Array<{
    description: string;
    domain: string;
  }> | null;
  pbxDomainsListFetchStatus: FETCH_STATUS;
  pbxDomainsPostStatus: FETCH_STATUS;
}

const INIT_STATE: ExternalSystemsState = {
  externalSystemsList: null,
  externalSystemsListFetchStatus: FETCH_STATUS.NULL,
  externalSystemInstanceDetails: null,
  externalSystemInstanceDetailsFetchStatus: FETCH_STATUS.NULL,
  globalExternalSystemsList: null,
  globalExternalSystemsListFetchStatus: FETCH_STATUS.NULL,
  systemMapperList: null,
  systemMapperListFetchStatus: FETCH_STATUS.NULL,
  carrierMapperList: null,
  carrierMapperListFetchStatus: FETCH_STATUS.NULL,
  csrpMapperList: null,
  csrpMapperListFetchStatus: FETCH_STATUS.NULL,
  carrierMapperDetailsPostStatus: FETCH_STATUS.NULL,
  carrierMapperDetailsPatchStatus: FETCH_STATUS.NULL,
  carrierMapperDetailsDeleteStatus: FETCH_STATUS.NULL,
  globalExternalSystemDetailsPatchStatus: FETCH_STATUS.NULL,
  externalSystemDetailsPostStatus: FETCH_STATUS.NULL,
  externalSystemDetailsDeleteStatus: FETCH_STATUS.NULL,
  externalSystemDetailsPatchStatus: FETCH_STATUS.NULL,
  externalSystemProvidersList: null,
  externalSystemProvidersListFetchStatus: FETCH_STATUS.NULL,
  externalSystemPBXList: null,
  externalSystemPBXListFetchStatus: FETCH_STATUS.NULL,
  routingConfigPostStatus: FETCH_STATUS.NULL,
  routingConfigDeleteStatus: FETCH_STATUS.NULL,
  csrpConfigDeleteStatus: FETCH_STATUS.NULL,
  routingConfigPatchStatus: FETCH_STATUS.NULL,
  pbxDomainsList: null,
  pbxDomainsListFetchStatus: FETCH_STATUS.NULL,
  pbxDomainsPostStatus: FETCH_STATUS.NULL,
};

const externalSystemsSlice = createSlice({
  name: 'externalSystems',
  initialState: INIT_STATE,
  reducers: {
    setExternalSystemsList: (state, action) => {
      state.externalSystemsList = action.payload;
    },
    setExternalSystemsListFetchStatus: (state, action) => {
      state.externalSystemsListFetchStatus = action.payload;
    },
    setExternalSystemInstanceDetails: (state, action) => {
      state.externalSystemInstanceDetails = action.payload;
    },
    setExternalSystemInstanceDetailsFetchStatus: (state, action) => {
      state.externalSystemInstanceDetailsFetchStatus = action.payload;
    },
    setGlobalExternalSystemsList: (state, action) => {
      state.globalExternalSystemsList = action.payload;
    },
    setGlobalExternalSystemsListFetchStatus: (state, action) => {
      state.globalExternalSystemsListFetchStatus = action.payload;
    },
    setSystemMapperList: (state, action) => {
      state.systemMapperList = action.payload;
    },
    setSystemMapperListFetchStatus: (state, action) => {
      state.systemMapperListFetchStatus = action.payload;
    },
    setCarrierMapperList: (state, action) => {
      state.carrierMapperList = action.payload;
    },
    setCarrierMapperListFetchStatus: (state, action) => {
      state.carrierMapperListFetchStatus = action.payload;
    },
    setCSRPMapperList: (state, action) => {
      state.csrpMapperList = action.payload;
    },
    setCSRPMapperListFetchStatus: (state, action) => {
      state.csrpMapperListFetchStatus = action.payload;
    },
    setCarrierMapperDetailsPostStatus: (state, action) => {
      state.carrierMapperDetailsPostStatus = action.payload;
    },
    setCarrierMapperDetailsPatchStatus: (state, action) => {
      state.carrierMapperDetailsPatchStatus = action.payload;
    },
    setCarrierMapperDetailsDeleteStatus: (state, action) => {
      state.carrierMapperDetailsDeleteStatus = action.payload;
    },
    setExternalSystemDetailsPostStatus: (state, action) => {
      state.externalSystemDetailsPostStatus = action.payload;
    },
    setExternalSystemDetailsDeleteStatus: (state, action) => {
      state.externalSystemDetailsDeleteStatus = action.payload;
    },
    setExternalSystemProvidersList: (state, action) => {
      state.externalSystemProvidersList = action.payload;
    },
    setExternalSystemProvidersListFetchStatus: (state, action) => {
      state.externalSystemProvidersListFetchStatus = action.payload;
    },
    setExternalSystemPBXList: (state, action) => {
      state.externalSystemPBXList = action.payload;
    },
    setExternalSystemPBXListFetchStatus: (state, action) => {
      state.externalSystemPBXListFetchStatus = action.payload;
    },
    setRoutingConfigPostStatus: (state, action) => {
      state.routingConfigPostStatus = action.payload;
    },
    setRoutingConfigDeleteStatus: (state, action) => {
      state.routingConfigDeleteStatus = action.payload;
    },
    setCSRPConfigDeleteStatus: (state, action) => {
      state.csrpConfigDeleteStatus = action.payload;
    },
    setRoutingConfigPatchStatus: (state, action) => {
      state.routingConfigPatchStatus = action.payload;
    },
    setExternalSystemDetailsPatchStatus: (state, action) => {
      state.externalSystemDetailsPatchStatus = action.payload;
    },
    setGlobalExternalSystemDetailsPatchStatus: (state, action) => {
      state.globalExternalSystemDetailsPatchStatus = action.payload;
    },
    setPbxDomainsList: (state, action) => {
      state.pbxDomainsList = action.payload;
    },
    setPbxDomainsListFetchStatus: (state, action) => {
      state.pbxDomainsListFetchStatus = action.payload;
    },
    setPbxDomainsPostStatus: (state, action) => {
      state.pbxDomainsPostStatus = action.payload;
    },
  },
  extraReducers: builder => {
    // GET EXTERNAL SYSTEMS
    builder
      .addCase(getExternalSystems.pending, state => {
        state.externalSystemsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getExternalSystems.rejected, (state, action) => {
        state.externalSystemsListFetchStatus = FETCH_STATUS.REJECTED;
        state.externalSystemsList = null;
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(getExternalSystems.fulfilled, (state, action) => {
        state.externalSystemsListFetchStatus = FETCH_STATUS.FULFILLED;
        state.externalSystemsList = action.payload;
      });

    builder
      .addCase(getGlobalExternalSystems.pending, state => {
        state.globalExternalSystemsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getGlobalExternalSystems.rejected, (state, action) => {
        state.globalExternalSystemsListFetchStatus = FETCH_STATUS.REJECTED;
        state.globalExternalSystemsList = null;
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(getGlobalExternalSystems.fulfilled, (state, action) => {
        state.globalExternalSystemsListFetchStatus = FETCH_STATUS.FULFILLED;
        state.globalExternalSystemsList = action.payload;
      });

    // GET EXTERNAL SYSTEM INSTANCE DETAILS
    builder.addCase(getExternalSystemInstanceDetails.pending, state => {
      state.externalSystemInstanceDetailsFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getExternalSystemInstanceDetails.rejected, (state, action) => {
      state.externalSystemInstanceDetailsFetchStatus = FETCH_STATUS.REJECTED;
      state.externalSystemInstanceDetails = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getExternalSystemInstanceDetails.fulfilled, (state, action) => {
      state.externalSystemInstanceDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      state.externalSystemInstanceDetails = action.payload;
    });

    // POST EXTERNAL SYSTEM
    builder.addCase(postExternalSystemDetails.pending, state => {
      state.externalSystemDetailsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postExternalSystemDetails.rejected, (state, action) => {
      state.externalSystemDetailsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postExternalSystemDetails.fulfilled, state => {
      state.externalSystemDetailsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('External system created successfully');
      });
    });

    // DELETE EXTERNAL SYSTEM
    builder.addCase(deleteExternalSystemDetails.pending, state => {
      state.externalSystemDetailsDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteExternalSystemDetails.rejected, (state, action) => {
      state.externalSystemDetailsDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteExternalSystemDetails.fulfilled, state => {
      state.externalSystemDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('External system deleted successfully');
      });
    });

    // CREATE CARRIER PLAN CONFIG
    builder
      .addCase(createCarrierPlanConfig.rejected, (_, action) => {
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(createCarrierPlanConfig.fulfilled, () => {
        setTimeout(() => snackbarUtils.success('Carrier/data plan mapping created successfully!'));
      });

    // CREATE CARRIER PLAN CONFIG
    builder
      .addCase(createCSRPConfig.rejected, (_, action) => {
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(createCSRPConfig.fulfilled, () => {
        setTimeout(() => snackbarUtils.success('CSRP mapping created successfully!'));
      });

    // UPDATE CARRIER PLAN CONFIG
    builder
      .addCase(updateCarrierPlanConfig.rejected, (_, action) => {
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(updateCarrierPlanConfig.fulfilled, () => {
        setTimeout(() => snackbarUtils.success('Carrier/data plan mapping updated successfully!'));
      });

    builder
      .addCase(updateCSRPConfig.rejected, (_, action) => {
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(updateCSRPConfig.fulfilled, () => {
        setTimeout(() => snackbarUtils.success('CSRP mapping updated successfully!'));
      });

    // DELETE CARRIER PLAN CONFIG
    builder
      .addCase(deleteCarrierPlanConfig.rejected, (_, action) => {
        setTimeout(() => snackbarUtils.error(action.payload ?? ''));
      })
      .addCase(deleteCarrierPlanConfig.fulfilled, () => {
        setTimeout(() => snackbarUtils.success('Carrier/data plan mapping deleted successfully!'));
      });

    // PATCH EXTERNAL SYSTEM
    builder.addCase(patchExternalSystemDetails.pending, state => {
      state.externalSystemDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchExternalSystemDetails.rejected, (state, action) => {
      state.externalSystemDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchExternalSystemDetails.fulfilled, state => {
      state.externalSystemDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('External system updated successfully');
      });
    });

    // PATCH GLOBAL EXTERNAL SYSTEM
    builder.addCase(patchGlobalExternalSystemDetails.pending, state => {
      state.globalExternalSystemDetailsPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchGlobalExternalSystemDetails.rejected, (state, action) => {
      state.globalExternalSystemDetailsPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchGlobalExternalSystemDetails.fulfilled, state => {
      state.globalExternalSystemDetailsPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Global external system updated successfully');
      });
    });

    // GET SYSTEM MAPPERS
    builder.addCase(getExternalSystemMapperList.pending, state => {
      state.systemMapperListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getExternalSystemMapperList.rejected, (state, action) => {
      state.systemMapperListFetchStatus = FETCH_STATUS.REJECTED;
      state.systemMapperList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getExternalSystemMapperList.fulfilled, (state, action) => {
      state.systemMapperListFetchStatus = FETCH_STATUS.FULFILLED;
      state.systemMapperList = action.payload;
    });

    builder.addCase(getExternalCarrierMapperList.pending, state => {
      state.carrierMapperListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getExternalCarrierMapperList.rejected, (state, action) => {
      state.carrierMapperListFetchStatus = FETCH_STATUS.REJECTED;
      state.carrierMapperList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getExternalCarrierMapperList.fulfilled, (state, action) => {
      state.carrierMapperListFetchStatus = FETCH_STATUS.FULFILLED;
      state.carrierMapperList = action.payload;
    });

    builder.addCase(getCSRPConfigsList.pending, state => {
      state.csrpMapperListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCSRPConfigsList.rejected, (state, action) => {
      state.csrpMapperListFetchStatus = FETCH_STATUS.REJECTED;
      state.csrpMapperList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getCSRPConfigsList.fulfilled, (state, action) => {
      state.csrpMapperListFetchStatus = FETCH_STATUS.FULFILLED;
      state.csrpMapperList = action.payload;
    });

    // GET EXTERNAL SYSTEM PROVIDERS
    builder.addCase(getExternalSystemsProviders.pending, state => {
      state.externalSystemProvidersListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getExternalSystemsProviders.rejected, (state, action) => {
      state.externalSystemProvidersListFetchStatus = FETCH_STATUS.REJECTED;
      state.externalSystemProvidersList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getExternalSystemsProviders.fulfilled, (state, action) => {
      state.externalSystemProvidersListFetchStatus = FETCH_STATUS.FULFILLED;
      state.externalSystemProvidersList = action.payload;
    });

    // GET EXTERNAL SYSTEM PBX
    builder.addCase(getExternalSystemsPBX.pending, state => {
      state.externalSystemPBXListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getExternalSystemsPBX.rejected, (state, action) => {
      state.externalSystemPBXListFetchStatus = FETCH_STATUS.REJECTED;
      state.externalSystemPBXList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getExternalSystemsPBX.fulfilled, (state, action) => {
      state.externalSystemPBXListFetchStatus = FETCH_STATUS.FULFILLED;
      state.externalSystemPBXList = action.payload;
    });

    // POST ROUTING CONFIG
    builder.addCase(postRouteConfig.pending, state => {
      state.routingConfigPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postRouteConfig.rejected, (state, action) => {
      state.routingConfigPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postRouteConfig.fulfilled, state => {
      state.routingConfigPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Routing config created successfully');
      });
    });

    // DELETE ROUTING CONFIG
    builder.addCase(deleteRouteConfig.pending, state => {
      state.routingConfigDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteRouteConfig.rejected, (state, action) => {
      state.routingConfigDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteRouteConfig.fulfilled, state => {
      state.routingConfigDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Routing config deleted successfully');
      });
    });

    // DELETE ROUTING CONFIG
    builder.addCase(deleteCSRPConfig.pending, state => {
      state.csrpConfigDeleteStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(deleteCSRPConfig.rejected, (state, action) => {
      state.csrpConfigDeleteStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(deleteCSRPConfig.fulfilled, state => {
      state.csrpConfigDeleteStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('CSRP config deleted successfully');
      });
    });

    // PATCH ROUTING CONFIG STATUS
    builder.addCase(patchRouteConfig.pending, state => {
      state.routingConfigPatchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(patchRouteConfig.rejected, (state, action) => {
      state.routingConfigPatchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(patchRouteConfig.fulfilled, state => {
      state.routingConfigPatchStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('Routing config status updated successfully');
      });
    });

    // GET PBX DOMAINS
    builder.addCase(getPBXDomainsList.pending, state => {
      state.pbxDomainsListFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getPBXDomainsList.rejected, (state, action) => {
      state.pbxDomainsListFetchStatus = FETCH_STATUS.REJECTED;
      state.pbxDomainsList = null;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(getPBXDomainsList.fulfilled, (state, action) => {
      state.pbxDomainsListFetchStatus = FETCH_STATUS.FULFILLED;
      state.pbxDomainsList = action.payload;
    });

    // POST PBX DOMAIN
    builder.addCase(postPBXDomains.pending, state => {
      state.pbxDomainsPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postPBXDomains.rejected, (state, action) => {
      state.pbxDomainsPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });
    builder.addCase(postPBXDomains.fulfilled, state => {
      state.pbxDomainsPostStatus = FETCH_STATUS.FULFILLED;
      setTimeout(() => {
        snackbarUtils.success('PBX domains imported successfully');
      });
    });
  },
});

export const {
  setExternalSystemDetailsPostStatus,
  setExternalSystemDetailsDeleteStatus,
  setExternalSystemPBXListFetchStatus,
  setExternalSystemProvidersList,
  setExternalSystemProvidersListFetchStatus,
  setExternalSystemPBXList,
  setExternalSystemsListFetchStatus,
  setGlobalExternalSystemsList,
  setRoutingConfigPostStatus,
  setGlobalExternalSystemsListFetchStatus,
  setRoutingConfigDeleteStatus,
  setRoutingConfigPatchStatus,
  setSystemMapperListFetchStatus,
  setCarrierMapperListFetchStatus,
  setCarrierMapperList,
  setCSRPMapperListFetchStatus,
  setCSRPMapperList,
  setCSRPConfigDeleteStatus,
  setCarrierMapperDetailsDeleteStatus,
  setCarrierMapperDetailsPostStatus,
  setCarrierMapperDetailsPatchStatus,
  setSystemMapperList,
  setExternalSystemsList,
  setExternalSystemDetailsPatchStatus,
  setGlobalExternalSystemDetailsPatchStatus,
  setPbxDomainsListFetchStatus,
  setPbxDomainsList,
  setPbxDomainsPostStatus,
} = externalSystemsSlice.actions;

export default externalSystemsSlice.reducer;
